/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { createContext } from 'react';
import { injectIntl } from 'react-intl';
import { useUiResources } from 'carbon-addons-iot-react';

type UIResourceContextType = {
  intl: any;
  userData: any;
  tenantId: string;
  children: any;
};
export const UiResourceContext = createContext(null);

// selector
const getManageURL = (ctx, fullPath = false) => {
  const path = ctx?.applications?.find(app => app.id === 'manage')?.href;
  return fullPath ? path : path?.split('/oslc/graphite')[0];
};
const getManageLocationURL = (ctx, name, fullPath = false) =>
  // https://main.maximo.com/maximo/ui?event=loadapp&value=LOCATION&forcereload=true&additionaleventvalue=location=LOCATION_NAME
  !name || fullPath
    ? getManageURL(ctx, !name || fullPath)
    : `${getManageURL(
        ctx,
        fullPath
      )}/ui?event=loadapp&value=LOCATION&forcereload=true&additionaleventvalue=location=${name}`;

const getManageAssetURL = (ctx, name, fullPath = false) =>
  // https://main.maximo.com/maximo/ui?event=loadapp&value=ASSET&forcereload=true&additionaleventvalue=assetnum=ASSET_NAME

  !name || fullPath
    ? getManageURL(ctx, !name || fullPath)
    : `${getManageURL(
        ctx,
        fullPath
      )}/ui?event=loadapp&value=ASSET&forcereload=true&additionaleventvalue=assetnum=${name}`;

/**
 * Small context to just load once the ui resource. It is wrapped in the logged routes
 * This is to get manage route if manage is installed so we can call properly `Open in Manage` route.
 *
 * @returns context provider
 */
const UiResourceContextProvider = ({
  intl: { locale },
  userData,
  tenantId,
  children,
}: UIResourceContextType): JSX.Element => {
  // Retrieve all the suiteHeaderData
  const [suiteHeaderData] = useUiResources({
    baseApiUrl: `https://api.${userData?.masDomain}`,
    lang: locale,
    surveyId: 'monitor',
    appId: 'monitor',
    workspaceId: tenantId,
  });

  return (
    <UiResourceContext.Provider value={[suiteHeaderData]}>{children}</UiResourceContext.Provider>
  );
};

export { getManageURL, getManageLocationURL, getManageAssetURL };
export default injectIntl(UiResourceContextProvider);
